import { ApolloError } from '@apollo/client';
import { Center, Spinner, Table, Text } from '@chakra-ui/react';
import ApplicationErrorView from '@packages/charka-common-ui/graphql/error/application-error-view';
import { useFocusedBranchContext } from '@packages/core/branch/focused-branch-context';
import { useTranslate } from '@tolgee/react';
import { toaster } from 'provider/src/module/chakra-ui/components/ui/toaster';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGetUniquePrintRule } from '../logic/use-get-unique-print-rule';
import { PrintRuleMenu } from '../print-rule-menu-list';

export default function MenuTable(props: {
  menus: PrintRuleMenu[] | undefined;
  loading: boolean;
  error?: ApolloError;
}) {
  const branch = useFocusedBranchContext();
  const { menus, loading, error } = props;
  
  const [generate, { loading: generateLoading }] = useGetUniquePrintRule();

  const [searchParams] = useSearchParams();
  const service = searchParams.get('service');

  const { t } = useTranslate();
  const navigate = useNavigate();

  if (loading || generateLoading) {
    return (
      <Center colorPalette={'teal'}>
        <Spinner m={4} colorPalette="blue.500" borderWidth="2px" />
      </Center>
    );
  }

  if (error) {
    return <ApplicationErrorView error={error} />;
  }

  if (!menus) {
    return <Text>{t('print-rule-menu.alert.error', 'Something went wrong with menu data')}</Text>;
  }

  const navigateToPrintRule = (menuId: string) => {
    if (!branch.branch) {
      return null;
    }

    generate({
      variables: {
        menuId: menuId,
        branchId: branch.branch.id,
      },
    })
      .then((result) => {
        if (result.data?.getUniquePrintRule.id) {
          navigate(`${menuId}/rule/${result.data.getUniquePrintRule.id}?service=${service ?? ''}`);
        }
      })
      .catch(() => {
        toaster.create({
          description: t('print-rule-unique-error', 'Something went wrong'),
          type: 'info',
        });
      });
  };

  return (
    <Table.Root size="lg" interactive>
      <Table.Header>
        <Table.Row bgColor={'bg.subtle'}>
          <Table.ColumnHeader fontWeight={600}>Product</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>

      <Table.Body>
        {menus.map((item) => (
          <Table.Row
            onClick={() => {
              navigateToPrintRule(item.id);
            }}
            key={item.id}
          >
            <Table.Cell>{item.title}</Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
